import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';

import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';

import dynamic from 'next/dynamic';
import { useInView } from 'react-intersection-observer';
import Link from 'next/link';

import { useEvent } from 'react-use';
import Image from './Image';

import {
  BlogPostContentFragment,
  BlogPostNavigationFragment,
} from '../schemas/api';

import SubscribeSection from './SubscribeSection';

import { Grid, columns, grid } from '../styles/grid';
import COLORS from '../styles/colors';
import TYPO from '../styles/typography';
import MarkdownStyle from '../styles/markdown';

import ShareSection from './ShareSection';
import LinkJSX from './Link';
import { divider } from '../styles/divider';
import ScrollShadow from './ScrollShadow';
import HandbookPostFirewall from './HandbookPostFirewall';

const BlogPostContent = dynamic(() => import('./BlogPostContent'), {
  ssr: false,
});

const Container = styled(Grid)`
  padding: 72px 0 36px;
  @media (min-width: 768px) {
    padding: 0 0 60px;
  }
`;

const PreviewText = styled.div`
  ${columns(1, 12)};
  margin: 0 auto;
  ${MarkdownStyle}

  ${TYPO.p1};
  font-size: 18px;
`;

const PreviewImage = styled.div`
  ${columns(1, 12)};
  padding: 0px 0px 48px;
  & > div {
    margin: 0 auto;
    overflow: hidden;
    border-radius: 6px;
  }
  & img {
    width: 100%;
    height: auto;
    margin: 0 auto;
  }

  display: flex;
  justify-content: center;

  & > div {
    border-radius: 0px;
  }

  @media (min-width: 1280px) {
    & > div {
      border-radius: 6px;
    }
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 36px;
`;

const Heading = styled.div`
  ${TYPO.h1};
`;

const MarkdownContent = styled.div`
  ${MarkdownStyle}

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    max-width: 100%;
    margin: 3em auto 0.5em;
    font-weight: 600;
  }

  h1 {
    font-size: 1.5em;
  }
  h2 {
    font-size: 1.3em;
  }
  h3 {
    font-size: 1.1em;
  }

  h5,
  h6 {
    font-size: 1em;
  }

  p {
    max-width: 100%;
    margin: 0.5em auto 0;
    line-height: 180%;
  }

  blockquote,
  ul,
  ol,
  dl,
  table,
  pre {
    max-width: 100%;
    margin: 1.5em 0 1.5em;
    line-height: 180%;
  }

  blockquote > p,
  pre > p {
    margin: 0;
  }

  font-size: 15px;

  @media (min-width: 768px) {
    font-size: 1em;

    & > div {
      border-radius: 6px;
    }
  }

  &[data-fade='true'] {
    mask-image: linear-gradient(to top, transparent 0px, #000 600px);
  }
`;

const DateContainer = styled.div`
  color: ${COLORS.shades.s300.css};
  ${TYPO.p2}
  font-size:12px;
`;

const Author = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
`;

const AuthorName = styled.span`
  ${TYPO.strong}
`;

type PictureProps = { $src: string };

const Picture = styled.div<PictureProps>`
  background-image: ${(props) => `url('${props.$src}')`};
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-size: cover;
`;

const Footer = styled.div`
  ${columns(1, 12)};
  padding: 160px 12px 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 72px;
`;

const Layout = styled.div`
  ${columns(1, 12)};
  ${grid(12)};
  position: relative;
  padding: 0 24px;
`;

const Sidebar = styled.div`
  ${columns(1, 12)};

  @media (min-width: 768px) {
    ${columns(1, 3)};
  }
`;

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 48px;

  @media (min-width: 768px) {
    position: sticky;
    top: 100px;
    max-height: calc(100vh - 100px);
    overflow: hidden;
  }
`;

const Content = styled.div`
  ${columns(1, 12)};

  @media (min-width: 768px) {
    ${columns(5, 7)};
  }
`;

const Chapter = styled.div`
  margin-top: 80px;
  padding: 100px 0 24px;
  ${divider('center')}
  &:after {
    background: transparent;
  }
`;

const ChapterTitle = styled.h2`
  ${TYPO.h1};
  font-weight: 700;
  margin-bottom: 24px;
`;

const ChaptersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  overflow: hidden;
  & > h3 {
    font-size: 12px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    font-weight: 600;
    line-height: 130%;
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  & > h2 {
    ${TYPO.h3};
  }
  border-bottom: 1px solid ${COLORS.shades.s200.css};
  padding-bottom: 24px;
`;

const SidebarScrollArea = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
  padding-right: 12px;
`;

const PostList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 30px;
  padding-bottom: 30px;
`;

const ChaptersList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 9px;

  padding: 6px 0 6px 18px;
  overflow: auto;
`;

const PostItem = styled.li`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 9px;
`;

const PostItemTitle = styled.div`
  ${TYPO.h3};
  font-size: 16px;
  padding-top: 12px;
`;

const PostItemPre = styled.div`
  ${TYPO.strong};
  font-size: 10px;
  padding: 2px 0;
  align-self: flex-start;
  border-radius: 2px;
  color: ${COLORS.brand.regular.css};
  text-transform: uppercase;
  letter-spacing: 2px;
  font-weight: 600;
`;

const ChaptersListItem = styled.li`
  ${TYPO.p2};

  font-weight: 500;

  &[aria-current='true'] > a {
    color: ${COLORS.brand.regular.css};
  }

  &[aria-current='true'] > a:hover,
  & > a:hover {
    color: ${COLORS.brand.regular.css};
    text-decoration: underline;
  }

  color: ${COLORS.blocks.text.css};

  & > span {
    color: ${COLORS.shades.s250.css};
    & > em {
      font-size: 14px;
      font-weight: 700;
    }
  }
`;

const HomeLink = styled(LinkJSX)`
  font-size: 14px;
  display: flex;
  gap: 3px;
  align-items: center;
  color: ${COLORS.brand.regular.css};

  &:hover {
    text-decoration: underline;
  }
`;

const ChapterImageContainer = styled.div`
  width: 100%;
  position: relative;
  margin-bottom: 60px;
`;

const ComingSoon = styled.span`
  ${TYPO.p2};
  font-weight: 400;
`;

const PostTitleComingSoon = styled.div`
  ${TYPO.p2}

  font-weight: 400;

  color: ${COLORS.shades.s250.css};
`;

type ChapterSectionProps = {
  onVisibilityChange: (
    inView: boolean,
    entry: IntersectionObserverEntry
  ) => void;
  children: JSX.Element;
};

function TrackSection({ children, onVisibilityChange }: ChapterSectionProps) {
  const { ref } = useInView({
    triggerOnce: false,
    onChange: onVisibilityChange,
    rootMargin: '-20% 0px -80% 0px',
  });

  return React.cloneElement(children, { ref });
}

type HandbookPostProps = {
  data: BlogPostContentFragment;
  navigation: BlogPostNavigationFragment[];
};

export default function HandbookPost({ data, navigation }: HandbookPostProps) {
  const [current, setCurrent] = useState<string | null>(null);

  const onLoad = useCallback(() => {
    if (!window.location.hash) return;
    const element = document.querySelector(window.location.hash);

    setTimeout(() => {
      element?.scrollIntoView();
    }, 0);
  }, []);

  useEvent('load', onLoad);

  return (
    <Container>
      {data.image && (
        <TrackSection
          onVisibilityChange={(inView) => {
            if (inView) setCurrent(null);
          }}
        >
          <PreviewImage>
            <Zoom
              zoomMargin={36}
              wrapStyle={{
                width: '100%',
                aspectRatio: data.image.width / data.image.height,
              }}
            >
              <Image
                src={data.image.url}
                width={data.image.width}
                height={data.image.height}
                alt={data.title}
                style={{ width: '100%', height: 'auto' }}
                priority
              />
            </Zoom>
          </PreviewImage>
        </TrackSection>
      )}
      <Layout>
        <Sidebar>
          <SidebarContent>
            <SidebarHeader>
              {data.author && (
                <Author>
                  <Picture $src={data.author.picture.url} />
                  <AuthorInfo>
                    <AuthorName>{data.author.name}</AuthorName>
                    <DateContainer>
                      {format(
                        new Date(data.publicationDate || data.sys.publishedAt),
                        'MMM dd, yyyy'
                      )}
                    </DateContainer>
                  </AuthorInfo>
                </Author>
              )}
            </SidebarHeader>
            <ChaptersSection>
              <h3>Table of contents</h3>
              <SidebarScrollArea>
                <ScrollShadow />
                <PostList>
                  {navigation.map((d) => {
                    return (
                      <PostItem key={d.sys.id}>
                        <PostItemTitle>
                          <PostItemPre>{d.preheading}</PostItemPre>
                          <span>{d.title}</span>
                          {d.comingSoon && (
                            <PostTitleComingSoon>
                              (Coming soon)
                            </PostTitleComingSoon>
                          )}
                        </PostItemTitle>

                        {d.chapters?.items?.length > 0 && (
                          <ChaptersList>
                            {d.chapters.items.map((c) => {
                              return (
                                <ChaptersListItem
                                  key={c.sys.id}
                                  aria-disabled={c.comingSoon}
                                  aria-current={current === c.slug}
                                >
                                  {c.comingSoon ? (
                                    <span>
                                      {c.title}{' '}
                                      <ComingSoon>(Coming soon)</ComingSoon>
                                    </span>
                                  ) : (
                                    <Link href={`/${d.slug}#${c.slug}`}>
                                      {c.title}
                                    </Link>
                                  )}
                                </ChaptersListItem>
                              );
                            })}
                          </ChaptersList>
                        )}
                      </PostItem>
                    );
                  })}
                </PostList>
              </SidebarScrollArea>
            </ChaptersSection>
          </SidebarContent>
        </Sidebar>
        <Content>
          <Header>
            <HomeLink link={{ url: '/handbook' }}>
              Handbook to Agile Systems Engineering
            </HomeLink>
            <Heading>{data.title}</Heading>
          </Header>
          {data.short && (
            <PreviewText suppressHydrationWarning>
              <ReactMarkdown linkTarget="_blank">{data.short}</ReactMarkdown>
            </PreviewText>
          )}
          {data.content && (
            <MarkdownContent>
              <BlogPostContent>{data.content}</BlogPostContent>
            </MarkdownContent>
          )}
          {data.contentTwo && (
            <MarkdownContent>
              <BlogPostContent>{data.contentTwo}</BlogPostContent>
            </MarkdownContent>
          )}
          {data.chapters.items.map((d) => {
            if (d.comingSoon) return null;

            return (
              <TrackSection
                key={d.sys.id}
                onVisibilityChange={(inView) => {
                  if (inView) setCurrent(d.slug);
                }}
              >
                <Chapter id={d.slug}>
                  {d.image && (
                    <ChapterImageContainer
                      style={{ aspectRatio: d.image.width / d.image.height }}
                    >
                      <Image
                        src={d.image.url}
                        alt={d.image?.title}
                        width={d.image.width}
                        height={d.image.height}
                        style={{
                          width: '100%',
                          height: 'auto',
                          objectFit: 'cover',
                        }}
                      />
                    </ChapterImageContainer>
                  )}
                  <ChapterTitle>{d.title}</ChapterTitle>

                  <MarkdownContent data-fade={d.locked}>
                    <BlogPostContent>{d.content}</BlogPostContent>
                  </MarkdownContent>
                  {d.locked && (
                    <HandbookPostFirewall
                      heading={d.lockedSectionHeading}
                      description={d.lockedSectionDescription}
                      typeformFormId={d.lockedActionTypeformFormId}
                      image={d.lockedSectionImage}
                    />
                  )}
                </Chapter>
              </TrackSection>
            );
          })}
        </Content>
      </Layout>
      <Footer>
        <SubscribeSection />
        <ShareSection />
      </Footer>
    </Container>
  );
}
